// Shared, pure "écart" (gap) detection for the Zone de chalandise product comparison (spec §5.2).
// No I/O here on purpose — this is the single source of truth for the rule, unit-tested directly,
// and reused as-is for both the online and offline comparison paths (there is no separate server-side
// "online" computation in this app — see the implementation plan's decision D2).

// Legacy statement product state enum (appointmentModel.js / appointmentTranslator.js): 0 means the
// product was never surveyed ("unknown"), not a real état — treat it the same as no data at all.
export const STATEMENT_STATE = Object.freeze({
	UNKNOWN: 0,
	PRESENT: 1,
	ABSENT: 2,
	OUT_OF_STOCK: 3,
});

function hasUsableState(product) {
	return product != null && product.state != null && product.state !== STATEMENT_STATE.UNKNOWN;
}

function hasUsableFacing(product) {
	return product != null && typeof product.facing === 'number' && Number.isFinite(product.facing);
}

/**
 * A product is "en écart" when the reference store is worse than the comparable set:
 * absent/out-of-stock while at least one comparable store has it present, or a facing strictly
 * below the best facing among the comparable stores.
 *
 * RG-01: a comparable store only weighs in when it actually holds data for the product. A store
 * whose état reads "—" (no statement row for it, or never surveyed) is ignored by BOTH rules — it
 * can neither satisfy the presence rule nor raise the facing bar with a facing that belongs to no
 * real relevé. Any surveyed état (présent, absent or rupture) counts.
 * @param {{state?: number|null, facing?: number|null}} referenceProduct
 * @param {Array<{state?: number|null, facing?: number|null}>} comparedProducts
 * @returns {boolean}
 */
export function isProductInGap(referenceProduct, comparedProducts = []) {
	const surveyedCompared = (comparedProducts || []).filter(hasUsableState);
	const referenceIsMissing = hasUsableState(referenceProduct) && (referenceProduct.state === STATEMENT_STATE.ABSENT || referenceProduct.state === STATEMENT_STATE.OUT_OF_STOCK);
	const anyComparedPresent = surveyedCompared.some((product) => product.state === STATEMENT_STATE.PRESENT);
	const missingVsComparedPresence = referenceIsMissing && anyComparedPresent;

	const comparedFacings = surveyedCompared.filter(hasUsableFacing).map((product) => product.facing);
	const bestComparedFacing = comparedFacings.length ? Math.max(...comparedFacings) : null;
	const facingBelowBest = hasUsableFacing(referenceProduct) && bestComparedFacing != null && referenceProduct.facing < bestComparedFacing;

	return missingVsComparedPresence || facingBelowBest;
}

/**
 * Trend direction for a compared store's own VMH/VMM cell, relative to the reference store's value
 * for the same product (see plan decision D5 — the icon lives on the compared cell, not the reference's).
 * @returns {'up'|'down'|null}
 */
export function getTrendDirection(referenceValue, comparedValue) {
	if (referenceValue == null || comparedValue == null) return null;
	if (comparedValue > referenceValue) return 'up';
	if (comparedValue < referenceValue) return 'down';
	return null;
}

function findByProductId(entries, productId) {
	return (entries || []).find((entry) => entry.productId === productId) || null;
}

function normalizedState(statementProduct) {
	return hasUsableState(statementProduct) ? statementProduct.state : null;
}

function normalizedFacing(statementProduct) {
	return hasUsableFacing(statementProduct) ? statementProduct.facing : null;
}

// Both the statement product and the catalog product default `productTreeId` to 0 when they carry
// no category at all (appointmentModel.js / productModel.js), so 0 has to be read as "none" —
// keeping it would group those products under a literal "0" category.
function usableProductTreeId(source) {
	const id = Number(source?.productTreeId);
	return Number.isFinite(id) && id > 0 ? id : null;
}

// A catalog product's own productTreeId is a LEAF node of Core/ProductTree (its specific product
// family/format, e.g. "Pur jus iPlus 750ml"), a different granularity from a shelf statement's
// productTreeId, which always sits at the org-configured survey depth (SFAStatementLevel/RankLevel,
// e.g. "TH") — both are ids in the same tree, so the leaf resolves fine in productTreesById, just to
// the wrong ancestor. Walk parentId up to the survey depth before using a catalog-only id as a
// grouping key, or a never-surveyed product lands in its own tiny leaf "category" instead of the real
// one its surveyed siblings share (see kpiService.js's _resolveStatementProductTree/_getProductTreeLevel
// for the same reconciliation done for KPI category columns).
function ancestorAtDepth(productTreesById, leafId, targetDepth) {
	if (leafId == null || targetDepth == null) return leafId;
	const maxIterations = Object.keys(productTreesById).length + 1;
	let currentId = leafId;
	let current = productTreesById[leafId] || null;
	let iterations = 0;
	while (current && Number(current.depth) > targetDepth && current.parentId != null && productTreesById[current.parentId] && iterations < maxIterations) {
		currentId = current.parentId;
		current = productTreesById[current.parentId];
		iterations += 1;
	}
	return currentId;
}

// A product can carry sell-out (VMH/VMM) history at any store without ever having been surveyed
// (état/facing/prix) at the reference store yet — e.g. a brand-new visited store whose relevé has
// only covered a handful of products so far. Driving rows off `referenceStatementProducts` alone
// silently dropped every such product's row entirely, which made its diffusion data invisible for
// EVERY store column, including the reference's own — not just the compared stores'. Union in every
// productId that appears anywhere (either store's statement, either store's diffusions) instead.
function collectProductIds({ referenceStatementProducts, referenceDiffusions, comparedStores, statementsByDeliveryLocationId, diffusionsByDeliveryLocationId }) {
	const ids = new Set();
	referenceStatementProducts.forEach((product) => ids.add(product.productId));
	referenceDiffusions.forEach((diffusion) => ids.add(diffusion.productId));
	comparedStores.forEach((store) => {
		(statementsByDeliveryLocationId[store.id] || []).forEach((product) => ids.add(product.productId));
		(diffusionsByDeliveryLocationId[store.id] || []).forEach((diffusion) => ids.add(diffusion.productId));
	});
	return ids;
}

/**
 * Assembles one comparison row per product referenced by any store's statement or sell-out
 * diffusions, joining in each comparable store's state/facing/price (from its latest statement) and
 * VMH/VMM (from its sell-out diffusions), and precomputing the écart flag. Pure — all data must
 * already be fetched by the caller.
 *
 * @param {object} params
 * @param {Array<object>} params.referenceStatementProducts - reference store's latest statement products
 * @param {Array<object>} params.referenceDiffusions - reference store's sell-out rows (per product)
 * @param {Array<{id:number, label?:string}>} params.comparedStores - comparable stores (id + display info)
 * @param {Record<number, Array<object>>} params.statementsByDeliveryLocationId - comparable stores' latest statement products, keyed by deliveryLocationId
 * @param {Record<number, Array<object>>} params.diffusionsByDeliveryLocationId - comparable stores' sell-out rows, keyed by deliveryLocationId
 * @param {Record<number, {label?:string, name?:string, ean?:string, reference?:string, productTreeId?:number}>} params.productsById
 * @param {Record<number, {label?:string, parentId?:number, depth?:number}>} [params.productTreesById] - productTree id -> tree node (Core/ProductTree), for the row-group label
 * @param {number|null} [params.statementCategoryDepth] - org-configured depth statements are surveyed at (SFAStatementLevel/RankLevel), used to walk a catalog-only productTreeId up to the same granularity as a surveyed category
 * @returns {Array<object>}
 */
export function buildComparisonRows({ referenceStatementProducts = [], referenceDiffusions = [], comparedStores = [], statementsByDeliveryLocationId = {}, diffusionsByDeliveryLocationId = {}, productsById = {}, productTreesById = {}, statementCategoryDepth = null }) {
	const productIds = collectProductIds({ referenceStatementProducts, referenceDiffusions, comparedStores, statementsByDeliveryLocationId, diffusionsByDeliveryLocationId });

	return Array.from(productIds).map((productId) => {
		const referenceProduct = findByProductId(referenceStatementProducts, productId);
		const productMeta = productsById[productId] || {};
		const referenceDiffusion = findByProductId(referenceDiffusions, productId);

		const reference = {
			state: referenceProduct ? normalizedState(referenceProduct) : null,
			facing: referenceProduct ? normalizedFacing(referenceProduct) : null,
			retailPrice: referenceProduct?.retailPrice ?? null,
			vmh: referenceDiffusion?.vmh ?? null,
			vmm: referenceDiffusion?.vmm ?? null,
		};

		const comparedStatements = comparedStores.map((store) => findByProductId(statementsByDeliveryLocationId[store.id], productId));

		const compared = comparedStores.map((store, index) => {
			const statement = comparedStatements[index];
			const diffusion = findByProductId(diffusionsByDeliveryLocationId[store.id], productId);
			return {
				deliveryLocationId: store.id,
				label: store.label || store.name || '',
				state: statement ? normalizedState(statement) : null,
				facing: statement ? normalizedFacing(statement) : null,
				retailPrice: statement?.retailPrice ?? null,
				vmh: diffusion?.vmh ?? null,
				vmm: diffusion?.vmm ?? null,
				vmhTrend: getTrendDirection(reference.vmh, diffusion?.vmh ?? null),
				vmmTrend: getTrendDirection(reference.vmm, diffusion?.vmm ?? null),
			};
		});

		// A product belongs to whichever category a relevé actually surveyed it in — and that relevé
		// isn't necessarily the reference store's: a product the visited store has never surveyed is
		// still surveyed in the zone's other stores. Reading the category off `referenceProduct` alone
		// left every such product uncategorized, which is what put them outside the grid's category
		// groups. The catalog product's own category is the last resort, and it's a LEAF id (see
		// ancestorAtDepth) — walked up to the survey depth so a never-surveyed product joins the same
		// category bucket a surveyed sibling would, rather than getting its own leaf-grain "category".
		const catalogProductTreeId = ancestorAtDepth(productTreesById, usableProductTreeId(productMeta), statementCategoryDepth);
		const productTreeId = usableProductTreeId(referenceProduct) ?? comparedStatements.map((statement) => usableProductTreeId(statement)).find((id) => id != null) ?? catalogProductTreeId ?? null;
		// `productTreeLabel` rides along on the statement (shelfStatementModel), so a category the
		// Core/ProductTree reference list doesn't cover still gets its real name instead of a raw id.
		const productTreeLabel = productTreesById[productTreeId]?.label || referenceProduct?.productTreeLabel || comparedStatements.find((statement) => usableProductTreeId(statement) === productTreeId)?.productTreeLabel || '';

		return {
			productId,
			label: productMeta.label || productMeta.name || '',
			ean: productMeta.ean || '',
			productReference: productMeta.reference || '',
			imageThumbnailUid: productMeta.imageThumbnailUid || productMeta.imageUid || null,
			category: productTreeLabel || (productTreeId != null ? String(productTreeId) : null),
			referenceStore: reference,
			compared,
			ecart: isProductInGap(reference, compared),
		};
	});
}
